import { ExcursionEditDialog } from "@/components/app/editor/excursions/ExcursionEditDialog";
import { ExcursionItem } from "@/components/app/editor/excursions/ExcursionItem";
import { useEnable } from "@/hooks/useEnable";
import { editorClient } from "@/lib/editorClient";
import { Trans } from "@lingui/react/macro";
import { Button } from "@op/sf-editor";
import { usePublished } from "@op/utils";
import { FeatherPlus } from "@subframe/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { redirect } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

export const Route = createFileRoute(
  "/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/$pageSlug/$versionId/excursions/",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const {
    versionId,
    organizationSlug,
    projectSlug,
    languageKey,
    topicSlug,
    pageSlug,
  } = Route.useParams();
  const queryClient = useQueryClient();

  const [editingExcursion, setEditingExcursion] = useState<
    string | undefined | null
  >(null);

  useEffect(() => {
    if (editingExcursion !== null) return;
    setTimeout(() => {
      document.body.style.pointerEvents = "auto";
    }, 1);
  }, [editingExcursion]);

  const published = usePublished();

  const { data: excursions } = useQuery(
    {
      queryKey: ["editor-version-excursions", { versionId }],
      queryFn: async () => {
        const res = await editorClient.versions[":versionId"].excursions.$get({
          param: {
            versionId,
          },
        });
        if (!res.ok) {
          if (res.status === 401) throw redirect({ to: "/login" });
          throw new Error("Failed to get excursions");
        }
        const data = await res.json();
        return data;
      },
    },
    queryClient,
  );

  const currentExcursion = useMemo(
    () => excursions?.find((excursion) => excursion.id === editingExcursion),
    [excursions, editingExcursion],
  );

  const canCreate = useEnable(organizationSlug, projectSlug, [
    "admin",
    "publisher",
    "editor",
  ]);

  const excursionCreateEnabled = useMemo(() => {
    return canCreate && !published;
  }, [canCreate, published]);

  return (
    <div className="w-full mx-auto flex flex-col gap-2 max-w-[1280px] px-4">
      <div className="flex w-full flex-col items-start gap-6">
        <div className="flex w-full items-center justify-end gap-4">
          <Button
            variant="brand-secondary"
            size="small"
            icon={<FeatherPlus />}
            onClick={() => setEditingExcursion(undefined)}
            disabled={!excursionCreateEnabled}
          >
            <Trans>Add Excursion</Trans>
          </Button>
        </div>
        <div className="flex w-full flex-col items-start gap-4">
          {excursions?.map((excursion) => {
            return (
              <ExcursionItem
                key={excursion.id}
                id={excursion.id}
                overline={excursion.overline}
                title={excursion.title}
                slug={excursion.slug}
                versionId={versionId}
                organizationSlug={organizationSlug}
                projectSlug={projectSlug}
                languageKey={languageKey}
                topicSlug={topicSlug}
                pageSlug={pageSlug}
                onEdit={setEditingExcursion}
              />
            );
          })}
        </div>
      </div>
      <ExcursionEditDialog
        excursionId={editingExcursion}
        overline={currentExcursion?.overline ?? undefined}
        title={currentExcursion?.title ?? undefined}
        onOpenChange={setEditingExcursion}
        versionId={versionId}
      />
    </div>
  );
}
